import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import Editor from "@monaco-editor/react";
import {
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import "../config.styles.css";
import "../../../assets/styles/styles.css";
import { useSnackbar } from "notistack";
import axios from "../../../utils/axiosInstance";
import { SYSTEM_CONFIG_TEMPLATES } from "../../../constants/configuration.const";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../utils/permissions";

const OPERATIONS_ENUM = {
    SAVE: "SAVE",
    EDIT: "EDIT",
};

const ConfigurationEditor = () => {
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const [operation, setOperation] = useState("");
    const [jsonConfig, setJsonConfig] = useState("");
    const [submitting, setSubmitting] = useState(true);
    const [validJson, setValidJson] = useState<boolean>(false);
    const [configDetails, setConfigDetails] = useState<any>(null);
    const editorRef = useRef<any>(null);
    const [loader, setLoader] = useState(false);
    const { hasPermission } = usePermissions();
    const [configVersions, setConfigVersions] = useState([]);
    const [platform, setPlatform] = useState("");
    const [configVersionLoader, setConfigVersionLoader] = useState(false);

    function handleEditorDidMount(editor: any, monaco: any) {
        editorRef.current = editor;
        // check if the operation is for save or edit
        if (location.pathname.includes("save-config")) {
            setOperation(OPERATIONS_ENUM.SAVE);
        } else {
            setOperation(OPERATIONS_ENUM.EDIT);
            getConfigDetailById();
        }
    }

    // getter for configID
    const getConfigId = () => queryParams.get("configId");

    // get the config details by Id
    const getConfigDetailById = () => {
        const configId: any = getConfigId();
        getConfigurationById(configId);
    };

    // make API call for getting the config details
    const getConfigurationById = async (id: string) => {
        try {
            setLoader(true);
            const config = await axios.get(
                process.env.BASE_URL + "api/v1/cms/config/" + id
            );
            if (config.data.success) {
                setConfigDetails(config.data?.data);
                jsonEditorBind(config.data?.data?.configJson);
                setPlatform(config.data?.data?.platform_lower);
                fetchConfigVersions(config.data?.data?.platform_lower);
            } else {
                enqueueSnackbar(config.data.message, { variant: "error" });
            }
        } catch (error) {
            console.log("Error fetchng the configuration", error);
            return null;
        } finally {
            setLoader(false);
        }
    };

    // check if json is valid & bind it in json editor
    const jsonEditorBind = (value: any) => {
        if (value) {
            editorRef.current.setValue(JSON.stringify(value));
            editorRef.current.getAction("editor.action.formatDocument").run();
        } else {
            enqueueSnackbar("No configuration found", { variant: "warning" });
        }
    };

    const onJsonConfigChange = (values: any) => {
        validateJson(values);
    };

    // validate the json typed
    const validateJson = (value: any) => {
        try {
            JSON.stringify(JSON.parse(value));
            setValidJson(true);
        } catch (error) {
            setValidJson(false);
        }
        setJsonConfig(value);
        return validJson;
    };

    //  JSON check
    const jsonCheck = (value: any) => {
        try {
            JSON.stringify(JSON.parse(value));
            return true;
        } catch (error) {
            return false;
        }
    };

    // get query params value
    const getQueryParamsValues = () => {
        if (operation === OPERATIONS_ENUM.SAVE) {
            return {
                profileName: queryParams.get("profileName"),
                internalName: queryParams.get("internalName"),
                platform: queryParams.get("platform"),
            };
        } else {
            return configDetails;
        }
    };

    const validateQueryParams = () => {
        const { profileName, internalName, platform }: any =
            getQueryParamsValues();
        if (profileName && internalName && platform) {
            return true;
        } else {
            return false;
        }
    };

    // When user clicks on the save config button, Make API call for storing in DB
    const saveConfiguration = async () => {
        try {
            const { profileName, internalName, platform }: any =
                getQueryParamsValues();
            const params = {
                profileName: profileName,
                platform: platform,
                internalName: internalName,
                configJson: JSON.parse(jsonConfig),
            };
            // Save in DB
            setSubmitting(false);
            setLoader(true);
            const saveConfigurationInDB = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/config`,
                { ...params }
            );

            if (saveConfigurationInDB.data?.success) {
                enqueueSnackbar("Configuration saved successfully!", {
                    variant: "success",
                });
                navigate("/");
            } else {
                enqueueSnackbar(saveConfigurationInDB.data?.message, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.log("Error saving configuration", error);
            enqueueSnackbar("Error when storing the configuration", {
                variant: "error",
            });
        } finally {
            setSubmitting(true);
            setLoader(false);
        }
    };

    // Validates the JSON & Saves
    const onClickSaveButton = async () => {
        if (!validJson) {
            enqueueSnackbar("Invalid JSON config, please check", {
                variant: "warning",
            });
        } else if (operation == OPERATIONS_ENUM.SAVE) {
            const validateParams = validateQueryParams();
            if (validateParams) {
                // save script to written
                await saveConfiguration();
            } else {
                enqueueSnackbar("Invalid values found in attributes", {
                    variant: "warning",
                });
            }
        }
    };

    // When user clicks on the edit config button, Make API call for updating in DB
    const editConfiguration = async () => {
        try {
            const params = JSON.parse(jsonConfig);
            const configId = getConfigId();
            // Save in DB
            setSubmitting(false);
            setLoader(true);
            const editConfigurationInDB = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/config/${configId}`,
                { ...params }
            );

            if (editConfigurationInDB.data?.success) {
                enqueueSnackbar("Configuration updated successfully!", {
                    variant: "success",
                });
                fetchConfigVersions(platform);
            } else {
                enqueueSnackbar(editConfigurationInDB.data?.message, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.log("Error saving configuration", error);
            enqueueSnackbar("Error when udpating the configuration", {
                variant: "error",
            });
        } finally {
            setSubmitting(true);
            setLoader(false);
        }
    };

    // Validates the Json & Edits
    const onClickEditButton = async () => {
        if (!validJson) {
            enqueueSnackbar("Invalid JSON config, please check", {
                variant: "warning",
            });
        } else if (operation == OPERATIONS_ENUM.EDIT) {
            const validateParams = validateQueryParams();
            if (validateParams) {
                // save script to written
                await editConfiguration();
            } else {
                enqueueSnackbar("Invalid values found in attributes", {
                    variant: "warning",
                });
            }
        }
    };

    // when user changes the template, populate in JSON editor
    const selectTemplates = (event: any) => {
        const template = event.target.value;
        const jsonConfig = SYSTEM_CONFIG_TEMPLATES.filter(
            (value) => value.appName === template
        );
        if (jsonConfig) {
            jsonEditorBind(jsonConfig[0]);
        }
    };

    const fetchConfigVersions = async (platform: string) => {
        try {
            setConfigVersionLoader(true);
            const response = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/config/versions`,
                {
                    params: { platform },
                }
            );

            if (response.data) {
                setConfigVersions(response.data);
            }
        } catch (error) {
            console.log("Error fetching configuration versions", error);
            enqueueSnackbar("Error fetching configuration versions", {
                variant: "error",
            });
            return [];
        } finally {
            setConfigVersionLoader(false);
        }
    };

    return (
        <>
            <div className="max-width">
                {/* Container for header components starts here */}
                <div className="d-flex j-space mb-1">
                    <div>
                        <h4>
                            {operation == OPERATIONS_ENUM.SAVE
                                ? "Save"
                                : "Edit"}{" "}
                            Configuration{" "}
                            {loader && <CircularProgress size={30} />}
                        </h4>
                        <h5>
                            Name - {getQueryParamsValues()?.profileName || ""}
                        </h5>
                    </div>
                    <div>
                        {/* Template dropdown starts here */}
                        <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                            <InputLabel id="demo-simple-select-label">
                                Templates
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                className="filter-platform-select"
                                id="demo-simple-select"
                                label="templateSelection"
                                onChange={selectTemplates}
                            >
                                {SYSTEM_CONFIG_TEMPLATES.map(
                                    (template, index): any => {
                                        return (
                                            <MenuItem
                                                key={index}
                                                value={template.appName}
                                            >
                                                {template.appName}
                                            </MenuItem>
                                        );
                                    }
                                )}
                            </Select>
                        </FormControl>
                        {/* Template dropdown ends here */}

                        {hasPermission(PERMISSIONS.config, ACTION.EDIT) &&
                            (operation == OPERATIONS_ENUM.SAVE ? (
                                <Button
                                    disabled={!submitting}
                                    onClick={onClickSaveButton}
                                    className="float-right mt-1"
                                    variant="contained"
                                >
                                    Save Config
                                </Button>
                            ) : (
                                <Button
                                    disabled={!submitting}
                                    onClick={onClickEditButton}
                                    className="float-right mt-1"
                                    variant="contained"
                                >
                                    Save Config
                                </Button>
                            ))}
                    </div>
                </div>
                {/* Container for header components ends here */}

                {/* JSON Editor starts here */}
                <Editor
                    theme="vs-dark"
                    height="80vh"
                    options={{
                        autoIndent: "brackets",
                        wrappingIndent: "indent",
                        wordWrap: "on",
                    }}
                    defaultLanguage="json"
                    onChange={onJsonConfigChange}
                    onMount={handleEditorDidMount}
                />
                {/* JSON Editor ends here */}

                {/* Version Table starts here */}
                <Typography className="mt-3" variant="body1">
                    Versions
                </Typography>
                {configVersionLoader ? (
                    <Typography variant="body2">Loading...</Typography>
                ) : configVersions.length === 0 ? (
                    <Typography variant="body2">
                        No versions available.
                    </Typography>
                ) : (
                    <TableContainer
                        className="mt-2 shadow-sm rounded-lg"
                        component={Paper}
                        sx={{ maxWidth: 700 }}
                    >
                        <Table className="version-table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Version Number</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Date of Edit</TableCell>
                                    <TableCell>Version Link</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {configVersions?.map((version: any) => {
                                    return (
                                        <TableRow key={version.configId}>
                                            <TableCell>
                                                v{version.versionNo.toFixed(1)}
                                            </TableCell>
                                            <TableCell>
                                                {version.modifiedBy || "-"}
                                            </TableCell>
                                            <TableCell>
                                                {new Date(
                                                    version.lastModified
                                                ).toLocaleString("en-US", {
                                                    month: "2-digit",
                                                    day: "2-digit",
                                                    year: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                    hour12: true,
                                                })}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    className="version-link-btn"
                                                    onClick={() =>
                                                        window.open(
                                                            `/json-viewer?configId=${version.configId}&module=config`,
                                                            "_blank"
                                                        )
                                                    }
                                                    variant="text"
                                                >
                                                    View Version
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {/* Version Table ends here */}
            </div>
        </>
    );
};

export default ConfigurationEditor;
